"use client";

import { useContentfulInspectorMode } from "@scott/contentful/live-preview-init";
import { PauseCircle, PlayCircle } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { Container } from "../../elements/container/container";
import { Image } from "../../elements/image/image";
import { Text } from "../../elements/text/text";

export interface HeroBannerProps {
  sys?: { id: string };

  /**
   * Main heading text for the banner
   */
  title: string;

  /**
   * Supporting text displayed below the title
   */
  subtitle?: string;

  /**
   * Brand logo configuration
   */
  logo?: {
    src: string;
    alt: string;
    type?: "Image" | "Video";
  };

  /**
   * Media asset (image or video) for the banner background
   */
  media:
    | {
        src: string;
        alt: string;
        type?: "Image" | "Video";
      }
    | {
        src: string;
        poster: string;
        type?: "Image" | "Video";
      };

  /**
   * Whether to apply Container wrapper for consistent spacing
   * @default true
   */
  fluid?: boolean;

  /**
   * Aspect ratio for the banner media
   * @default "3:1"
   */
  aspectRatio?: "3:1" | "16:9";
}

export function HeroBanner(props: Readonly<HeroBannerProps>): ReactNode {
  const entryId = props?.sys?.id;
  const inspectorProps = useContentfulInspectorMode({ entryId });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const fluid = props.fluid ?? true;
  const aspectRatio = props.aspectRatio ?? "3:1";

  // Determine if we should show video based on type or poster property
  const isVideoType = props.media.type === "Video" || "poster" in props.media;
  const showVideo = isVideoType && !videoError;

  const togglePlay = () => {
    if (!videoRef.current) {
      return;
    }

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Determine alt text for media
  let mediaAlt = "Banner image";
  if ("alt" in props.media) {
    mediaAlt = props.media.alt;
  } else if ("poster" in props.media) {
    mediaAlt = props.media.poster;
  }

  const content = (
    <section
      className={`relative z-0 aspect-[1/1.414] w-full overflow-hidden rounded-md md:aspect-[16/9] ${aspectRatio === "3:1" ? "lg:aspect-[3/1]" : "lg:aspect-[16/9]"}`}
    >
      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        {showVideo ? (
          <video
            aria-label={props.media.src}
            autoPlay
            className="h-full w-full object-cover"
            loop
            muted
            onError={() => setVideoError(true)}
            playsInline
            poster={"poster" in props.media ? props.media.poster : undefined}
            ref={videoRef}
          >
            <source src={props.media.src} type="video/mp4" />
          </video>
        ) : (
          <Image
            alt={mediaAlt}
            aspectRatio={aspectRatio}
            fill
            priority
            src={props.media.src}
          />
        )}
        {/* Top gradient */}
        <div className="container absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black/70 to-transparent" />
        {/* Bottom gradient */}
        <div className="container absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
      </div>

      {showVideo && (
        <button
          className="absolute right-10 bottom-10 z-30 cursor-pointer"
          onClick={togglePlay}
        >
          {isPlaying ? (
            <PauseCircle color="white" size={64} strokeWidth={0.5} />
          ) : (
            <PlayCircle color="white" size={64} strokeWidth={0.5} />
          )}
        </button>
      )}

      {/* Content Container */}
      <div className="relative z-20 flex min-h-full flex-col items-start gap-4 px-4 py-14 md:p-14">
        {/* Logo */}
        {props.logo && (
          <div className="h-14 w-auto">
            <Image
              alt={props.logo.alt}
              aspectRatio="1:1"
              objectFit="contain"
              objectPosition="bottom left"
              priority
              src={props.logo.src}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex max-w-[500px] flex-col gap-2 text-left">
          {/* Title */}
          <Text
            color="white"
            variant="h2"
            {...inspectorProps({ fieldId: "title" })}
          >
            {props.title}
          </Text>

          {/* Subtitle */}
          {props.subtitle && (
            <Text
              color="white"
              variant="lead"
              {...inspectorProps({ fieldId: "subtitle" })}
            >
              {props.subtitle}
            </Text>
          )}
        </div>
      </div>
    </section>
  );

  return fluid ? <Container>{content}</Container> : content;
}
